import { FC, useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import CloseIcon from "@mui/icons-material/Close";
import styles from "@/components/general/youTubePlayer.module.css";
import { extractYouTubeVideoId } from "@/helper/youTubePlayerUtils";
import GenericConfirmationModal from "@/components/forms/comfirmations/genericConfirmationModal";
import { setYouTubeConsent } from "@/components/state/youtube/youtubeConsentSlice";
import { RootState } from "@/components/state/store";

interface YouTubePlayerProps {
  url: string;
  requireConsent?: boolean;
  customConsentMessage?: string;
  isOpen: boolean;
  onClose: () => void;
}

const YouTubePlayer: FC<YouTubePlayerProps> = ({
  url,
  requireConsent = false,
  customConsentMessage,
  isOpen,
  onClose
}) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const hasConsented = useSelector((state: RootState) => state.youtubeConsent.hasConsented);
  const [showConsent, setShowConsent] = useState(requireConsent && !hasConsented);

  const consentMessage = customConsentMessage || (() => {
    const message = t("youtube_consent_message");
    const currentLanguage = i18n.language || "en";

    const termsUrl = currentLanguage === "de"
      ? "https://www.youtube.com/t/terms?hl=de&override_hl=1"
      : "https://www.youtube.com/static?template=terms";

    const linkRegex = /<link>(.*?)<\/link>/g;
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = linkRegex.exec(message)) !== null) {
      if (match.index > lastIndex) {
        parts.push(message.slice(lastIndex, match.index));
      }

      parts.push(
        <a
          key={match.index}
          href={termsUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.youtubeTermsLink}
        >
          {match[1]}
        </a>
      );

      lastIndex = match.index + match[0].length;
    }

    if (lastIndex < message.length) {
      parts.push(message.slice(lastIndex));
    }

    return <span>{parts}</span>;
  })();

  const videoId = useMemo(() => {
    return extractYouTubeVideoId(url);
  }, [url]);

  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscKey);
    }

    return () => {
      document.removeEventListener("keydown", handleEscKey);
    };
  }, [isOpen]);

  useEffect(() => {
    if (requireConsent && !hasConsented) {
      setShowConsent(true);
    } else {
      setShowConsent(false);
    }
  }, [requireConsent, hasConsented, isOpen]);

  const handleConsent = () => {
    dispatch(setYouTubeConsent(true));
    setShowConsent(false);
  };

  const handleClose = () => {
    setShowConsent(requireConsent && !hasConsented);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      {!showConsent && isOpen && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <GenericButton
              variant="icon"
              color="white"
              className={styles.closeButton}
              onClick={handleClose}
            >
              <CloseIcon />
            </GenericButton>

            <GenericButton
              variant="outlined"
              color="white"
              className={styles.mobileCloseButton}
              onClick={handleClose}
            >
              {t("close")}
            </GenericButton>

            {videoId ? (
              <div className={styles.videoContainer}>
                <iframe
                  src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`}
                  title={t("youtube_video_player_title")}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className={styles.videoFrame}
                />
              </div>
            ) : (
              <div className={styles.errorMessage}>
                <p>{t("youtube_invalid_url_error")}</p>
                <p>{t("youtube_invalid_url_hint")}</p>
              </div>
            )}
          </div>
        </div>
      )}

      <GenericConfirmationModal
        isOpen={showConsent}
        title={t("youtube_consent_title")}
        message={consentMessage}
        confirmButtonText={t("youtube_consent_accept")}
        cancelButtonText={t("decline")}
        onConfirm={handleConsent}
        onCancel={handleClose}
      />
    </>
  );
};

export default YouTubePlayer;
